import { useContext, useMemo } from "react";
import {
  DataGridContainerContext,
  IDataGridContainerContext,
} from "./DataGrid";

import { SortAscIcon, SortDescIcon } from "@primer/octicons-react";
import { Box } from "@primer/react";
import clsx from "clsx";
import { useCommonContext } from "../../contexts/common";
import MyTextInput from "../ui/text-input/TextInput";
import styles from "./DataGrid.module.css";
import FilterModeSelection from "./FilterModeSelection";
import HeaderFilterButton from "./toolbar/HeaderFilterButton";
import { GetDataGridHederGroupCell } from "./type/ColumnHelper";
import { ICondition, IDataGridColumn, IDataGridSort } from "./type/IDataGridProps";
interface IHeaderProps {
  stickyOffsets: any;
  dataColumns: IDataGridColumn[];
  sorting?: IDataGridSort;
  onSortingChanged?: (sorting: IDataGridSort) => void;
  dataSource: any[],
  conditions: ICondition[]
}
const Header = (props: IHeaderProps) => {
  const {
    _columns,
    filterRow,
    _onColumnSettingChanged,
    _conditions: conditions,
    _setConditions: setConditions,
  } = useContext<IDataGridContainerContext>(DataGridContainerContext);
  const { stickyOffsets, dataColumns } = props;
  const { translate } = useCommonContext();
  const headerGroups = useMemo(() => {
    return GetDataGridHederGroupCell(_columns);
  }, [_columns]);

  const adjustColumnWidth = (
    col: IDataGridColumn,
    newWidth: number,
    colUuid: string,
  ): IDataGridColumn => {
    let result = { ...col };
    if (col.uuid === colUuid) {
      result.width = newWidth;
      return result;
    }
    if ((col.columns?.length ?? 0) > 0) {
      result.columns = (col.columns ?? []).map((sub) => {
        return adjustColumnWidth(sub, newWidth, colUuid);
      });

      result.width = result.columns
        .map((x) => x.width as number)
        .reduce((a, b) => a + b, 0);
      return result;
    }
    return result;
  };

  const handleColumnResize = (columnId: string, newWidth: number) => {

    _onColumnSettingChanged(
      _columns.map((col) => {
        return adjustColumnWidth(col, newWidth, columnId);
      }),
    );
  };

  const onFilterChange = (field: any, operator?: any, value?: any) => {
    const cond = conditions.find((x) => x.field === field);
    if (cond) {
      updateCondition(field, operator, value ?? "");
    } else {
      addCondition(field, operator, value);
    }
  };
  // Thêm điều kiện mới
  const addCondition = (field: any, operator?: any, value?: any) => {
    setConditions([
      ...conditions.filter((x) => x.field && x.field !== field),
      { field: field, operator: operator ?? "equals", value: value ?? "" },
    ]);
  };

  // Xóa điều kiện
  const removeCondition = (field: any) => {
    const newConditions = conditions.filter((x) => x.field !== field);
    setConditions([...newConditions]);
  };
  // Cập nhật điều kiện
  const updateCondition = (field: any, operator?: any, value?: any) => {
    setConditions(
      [...conditions].map((c) => {
        if (c.field === field) {
          return {
            ...c,
            operator: operator ?? "equals",
            value,
          };
        }
        return c;
      }),
    );
  };

  return (
    <thead
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
      className={headerGroups.length > 1 ? styles.bands : ""}
    >
      {headerGroups.map((headerGroup, hIdx) => {
        return (
          <tr key={hIdx}>
            {headerGroup.cells.map((column, cIdx) => {
              const uuid = column.uuid ?? column.dataField ?? column.id ?? "";
              const isLastRow = headerGroups.length === hIdx + 1;
              const sortMode =
                props.sorting && props.sorting?.enable !== false && column.isAllowSort !== false && props.sorting.field === column.dataField
                  ? props.sorting.mode
                  : undefined;
              return (
                <th
                  rowSpan={column.rowspan}
                  colSpan={column.colspan}
                  key={`${hIdx}-${cIdx}`}
                  className={clsx(
                    column.fixed ? styles.fixed : "",
                    column.fixed === "left" ? styles.left : "",
                    column.fixed === "right" ? styles.right : "",
                    column.isLastColFixed ? styles.lastFixed : "",
                  )}
                  style={{
                    textAlign: column.align ?? "left",
                    width: column.width,
                    minWidth: column.minWidth,
                    position: column.fixed ? "sticky" : "relative",
                    left:
                      column.fixed === "left"
                        ? stickyOffsets.leftOffsets[uuid]
                        : undefined,
                    right:
                      column.fixed === "right"
                        ? stickyOffsets.rightOffsets[uuid]
                        : undefined,
                    zIndex: column.fixed ? 20 : 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      width: "100%",
                      alignItems: "center",
                      justifyContent:
                        column.align === "center" ? "center" : undefined,
                      flexDirection:
                        column.align === "right" ? "row-reverse" : undefined,
                    }}
                  >
                    <Box sx={{
                      cursor: "pointer",
                      display: "flex",
                      gap: 1,
                      // flex: 1
                    }}
                      onClick={() => {
                        if (column.dataField && column.isAllowSort !== false)
                          props.onSortingChanged?.({
                            ...props.sorting,
                            field: column.dataField,
                            mode: sortMode === "asc" ? "desc" : "asc",
                          } as any);
                      }}
                    >
                      <Box>{column.headerRender
                        ? column.headerRender()
                        : translate(column.caption as string)}
                      </Box>
                      <Box>
                        {sortMode &&
                          <>
                            {sortMode === "asc"
                              ? <SortAscIcon />
                              : <SortDescIcon />}
                          </>
                        }
                      </Box>
                    </Box>
                    {column.dataField && !column.isDisableHederFilter &&
                      <Box>
                        <HeaderFilterButton dataSource={props.dataSource}
                          dataColumn={column}
                          conditions={props.conditions}
                          onApply={(values) => {
                            if (values.length > 0) {
                              onFilterChange(
                                column.dataField,
                                "includes",
                                values
                              );
                            } else {
                              removeCondition(column.dataField)
                            }
                          }}
                        />
                      </Box>
                    }
                  </Box>

                  {column.dataField && (
                    <Box
                      onMouseDown={(e) => {
                        e.persist();

                        // Lấy width hiện tại
                        const startWidth = column.width as number;
                        const startX = e.clientX;

                        const onMouseMove = (e2: MouseEvent) => {
                          const delta = e2.clientX - startX;
                          const newWidth = startWidth + delta;

                          handleColumnResize(column.uuid as string, newWidth);
                        };

                        const onMouseUp = () => {
                          document.removeEventListener(
                            "mousemove",
                            onMouseMove,
                          );
                          document.removeEventListener("mouseup", onMouseUp);
                        };

                        document.addEventListener("mousemove", onMouseMove);
                        document.addEventListener("mouseup", onMouseUp);
                      }}
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        height: "100%",
                        width: "5px",
                        cursor: "col-resize",
                        userSelect: "none",
                        touchAction: "none",
                      }}
                    ></Box>
                  )}
                </th>
              );
            })}
          </tr>
        );
      })}
      {(filterRow?.enable === true || !filterRow) &&
        headerGroups
          .filter((x, idx) => idx === headerGroups.length - 1)
          .map((headerGroup: any, hIdx) => {
            return (
              <tr key={hIdx}>
                {dataColumns.map((column, idx: number) => {
                  const columnCondition = conditions.find(
                    (x) => x.field === column.dataField,
                  );
                  const uuid =
                    column.uuid ?? column.dataField ?? column.id ?? "";
                  return (
                    <th
                      style={{
                        textAlign: column.align ?? "left",
                        width: column.width,
                        minWidth: column.minWidth,
                        position: column.fixed ? "sticky" : "relative",
                        left:
                          column.fixed === "left"
                            ? stickyOffsets.leftOffsets[uuid]
                            : undefined,
                        right:
                          column.fixed === "right"
                            ? stickyOffsets.rightOffsets[uuid]
                            : undefined,
                        zIndex: column.fixed ? 20 : 1,
                        backgroundColor: "#fff",
                      }}
                      className={clsx(
                        column.fixed ? styles.fixed : "",
                        column.fixed === "left" ? styles.left : "",
                        column.fixed === "right" ? styles.right : "",
                        column.isLastColFixed ? styles.lastFixed : "",
                      )}
                      key={`F${hIdx}-${idx}`}
                    >
                      <Box sx={{ margin: "-0.5rem -0.75rem" }}>
                        {column.dataField && (
                          <MyTextInput
                            block
                            leadingVisual={
                              <Box sx={{ color: "#656d76", m: -2 }}>
                                <FilterModeSelection
                                  value={columnCondition?.operator}
                                  onChanged={(operator) => {
                                    onFilterChange(
                                      column.dataField,
                                      operator,
                                      columnCondition?.value ?? "",
                                    );
                                  }}
                                />
                              </Box>
                            }
                            sx={{
                              backgroundColor: "transparent",
                              boxShadow: "none",
                              border: "0!important",
                              mt: "1px",
                              borderRadius: "0!important",
                            }}
                            onValueDelayChanged={(value) => {
                              onFilterChange(
                                column.dataField,
                                columnCondition?.operator ?? "contains",
                                value,
                              );
                            }}
                          />
                        )}
                      </Box>
                    </th>
                  );
                })}
              </tr>
            );
          })}
    </thead>
  );
};

export default Header;
